import React, { useState } from 'react';
import { api } from '../api';
import { useToast } from '../ToastContext';

/**
 * QuoteSheetUpload — import a supplier's filled-in quote sheet (finding #11).
 *
 * Two-step by design: upload parses and previews, and only an explicit Import
 * writes anything. A mis-parsed sheet must not silently create a revision.
 */
export default function QuoteSheetUpload({ envelope, onClose, onImported }) {
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  async function doParse(f) {
    setBusy(true);
    setPreview(null);
    try {
      const res = await api.uploadQuoteSheet(envelope._id, f);
      setPreview(res);
      if ((res.preview || []).length === 0) {
        toast.push('No quoted lines found in that sheet.', { variant: 'error' });
      }
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function doImport() {
    setBusy(true);
    try {
      const res = await api.commitQuoteSheet(envelope._id, preview.preview);
      toast.push(`Imported ${res.created} line${res.created === 1 ? '' : 's'} as revision ${res.items?.[0]?.revision ?? 1}`);
      onImported && onImported();
      onClose();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  const rows = preview?.preview || [];
  const errors = preview?.errors || [];
  const warnings = preview?.warnings || [];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,10,30,0.4)' }} onClick={onClose} />
      <div
        style={{
          position: 'absolute', top: '6%', left: '50%', transform: 'translateX(-50%)',
          width: 'min(900px, 92vw)', maxHeight: '86vh', overflowY: 'auto',
          background: '#fff', borderRadius: 10, boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ padding: 20, borderBottom: '1px solid var(--border-1)' }}>
          <h2 className="card-title">Upload quote file — {envelope.supplier?.name}</h2>
          <div className="subtitle">
            Upload the RFQ spreadsheet we sent, with the response columns filled in
            (UnitPrice, YourQtyQuoted, lead time, manufacturer, condition, CoC).
          </div>
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              aria-label="Quote spreadsheet"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setFileName(f?.name || '');
                if (f) doParse(f);
              }}
            />
            {fileName && !busy && <span className="chip muted">{fileName}</span>}
            {busy && <span className="subtitle">Working…</span>}
          </div>

          {preview && (
            <>
              <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span className="chip ready">{rows.length} line{rows.length === 1 ? '' : 's'} ready</span>
                {errors.length > 0 && <span className="chip risk">{errors.length} row{errors.length === 1 ? '' : 's'} skipped</span>}
                {warnings.length > 0 && <span className="chip review">{warnings.length} needing an FX rate</span>}
                <span className="chip muted">sheet: {preview.sheetName}</span>
                <span className="chip muted">RFQ currency: {preview.baseCurrency}</span>
              </div>

              {errors.length > 0 && (
                <div className="banner risk" style={{ marginTop: 12 }}>
                  <b>Rows that could not be imported</b>
                  <ul style={{ margin: '6px 0 0 18px' }}>
                    {errors.map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
                  </ul>
                </div>
              )}

              {warnings.length > 0 && (
                <div className="banner review" style={{ marginTop: 12 }}>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {warnings.map((w, i) => <li key={i}>{w.message}</li>)}
                  </ul>
                </div>
              )}

              {rows.length > 0 && (
                <table className="table" style={{ marginTop: 14 }}>
                  <thead>
                    <tr>
                      <th>Line</th><th>Part No</th><th>Qty</th><th>Unit price</th>
                      <th>Mfr</th><th>Condition</th><th>Lead</th><th>CoC</th><th>Matched by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={String(r.rfqLineItemId)}>
                        <td>{r.lineNumber}</td>
                        <td className="mono">{r.quotedPartNumber}</td>
                        <td className="mono">{r.qtyQuoted}</td>
                        <td className="mono">
                          {r.currency} {Number(r.unitPrice).toFixed(2)}
                          {r.currency !== preview.baseCurrency && (
                            r.fxRate > 0
                              ? <span style={{ color: 'var(--ink-500)' }}> @{r.fxRate}</span>
                              : <span className="chip risk" style={{ fontSize: 9, marginLeft: 4 }}>no rate</span>
                          )}
                        </td>
                        <td>{r.manufacturerQuoted || '—'}</td>
                        <td>{r.conditionQuoted || '—'}</td>
                        <td className="mono">
                          {r.leadTimeValue != null ? `${r.leadTimeValue}${(r.leadTimeUnit || '')[0]?.toLowerCase() || ''}` : '—'}
                        </td>
                        <td>{r.cocAvailable ? (r.cocType || 'Yes') : '—'}</td>
                        <td><span className="chip muted" style={{ fontSize: 10 }}>{r.matchedBy}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        <div style={{ padding: 16, borderTop: '1px solid var(--border-1)', display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={doImport} disabled={busy || rows.length === 0}>
            {busy ? 'Importing…' : `Import ${rows.length} line${rows.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
